/**
 * ==========================================================================
 *  Everything in the level with wheels under it.
 *
 *  Cars, motorhomes, the badges that tell two of them apart at fifty metres,
 *  and the charging cable that ties one of them to the tolppa. The tractor and
 *  its trailer are not here - they are `src/render/FarmVehicle.ts`, because a
 *  tractor shares nothing with a car but the word.
 *
 *  A class rather than free functions, because the caching is the whole point.
 *  The parking lot is 41 cars that differ in paint, registration and whether
 *  the boot steps down; built naively that is 41 bodies, 164 wheels and 41
 *  sets of glass. So a *template* is keyed on everything that changes the
 *  shape - material, dimensions, badge, boot - and cloned per bay, and the
 *  registration goes on the clone afterwards. Only the plate texture is per
 *  car, and a plate is two triangles.
 *
 *  Scene-free, like `People`: it hands back a group and the caller decides
 *  where that group goes.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { RINGS_ASPECT, buildPlate, buildRings, buildRoundel } from './decals';
import type { SceneAssets } from './SceneAssets';
import { boxCenter, boxSize, type CarBadge, type MaterialKey, type SolidSpec } from '../world/Level';

/**
 * Top of a car's doors and wings, as a fraction of its height: where the paint
 * stops and the glass starts.
 */
export const CAR_BELTLINE = 0.56;
/**
 * Where a registration plate sits up the nose and the tail, as a fraction of
 * the vehicle's height.
 *
 * On a car that is the bumper, well under the beltline. A motorhome's
 * windscreen comes down a good deal further than a car's, so its plate has to
 * go lower still or it ends up stuck to the glass - which is why the two are
 * different numbers rather than one shared one.
 */
export const CAR_PLATE_Y = CAR_BELTLINE * 0.4;
export const CAMPER_PLATE_Y = 0.24;

export class Vehicles {
  /** Assembled vehicles, keyed by material and size, cloned per parking bay. */
  private readonly vehicles = new Map<string, THREE.Group>();
  /** One plate texture per registration, keyed by the registration itself. */
  private readonly plates = new Map<string, THREE.Material>();
  private roundelMaterial: THREE.Material | null = null;
  private ringsMaterial: THREE.Material | null = null;

  constructor(private readonly assets: SceneAssets) {}

    /**
     * Places one vehicle. The collider is the box; this is what you see.
     *
     * Everything is built along local +X and turned, because a car in a bay lies
     * along z and a car in the ansalanka row lies along x, and they are otherwise
     * the same car.
     */
    build(spec: SolidSpec, isCamper: boolean): THREE.Group {
      const size = boxSize(spec.box);
      const centre = boxCenter(spec.box);
      const alongZ = size.z > size.x;
      const length = alongZ ? size.z : size.x;
      const width = alongZ ? size.x : size.z;

      // Which end of the *local* car the boot lid is on. Everything is built
      // along local +X and turned, and a +PI/2 turn about Y sends local +X to
      // world -Z - so on a car lying along z the sides are the other way round.
      // Getting this backwards would draw the step-down at the windscreen and
      // leave the collider's low end looking like a roof.
      const boot = spec.boot;
      const bootSide = boot ? ((alongZ ? -boot.end : boot.end) as -1 | 1) : 0;

      // The badge and the boot are part of the key: two cars that differ in
      // either are different cars, and sharing one template between them would
      // give both of them whichever was built first. The registration is not,
      // and that is deliberate - see below.
      const key = `${isCamper ? 'camper' : 'car'}|${spec.material}|${length.toFixed(2)}|${width.toFixed(2)}|${size.y.toFixed(2)}|${spec.badge ?? ''}|${boot ? `${boot.length.toFixed(2)}:${boot.height.toFixed(2)}:${bootSide}` : ''}`;
      let template = this.vehicles.get(key);
      if (!template) {
        template = isCamper
          ? this.buildCamper(spec.material, length, width, size.y)
          : this.buildCar(spec.material, length, width, size.y, spec.badge, boot && bootSide !== 0
              ? { length: boot.length, height: boot.height, side: bootSide }
              : null);
        this.vehicles.set(key, template);
      }

      const group = template.clone();
      // The plates go on the clone rather than into the template, because every
      // vehicle in the level has a different registration and no two of them are
      // otherwise different at all. Keyed into the template it would be a
      // separate copy of a whole car - body, glass, wheels, arches, lights - per
      // plate, which is forty-odd cars' worth of geometry to letter forty bumpers
      // that are two triangles each. Only the texture is per-registration; see
      // `mountPlates`.
      if (spec.plate) {
        this.mountPlates(group, length, size.y * (isCamper ? CAMPER_PLATE_Y : CAR_PLATE_Y), spec.plate);
      }
      group.position.set(centre.x, spec.box.minY, centre.z);
      group.rotation.y = alongZ ? Math.PI / 2 : 0;
      group.name = spec.name;
      return group;
    }

    /**
     * A car: body, greenhouse, boot lid, four wheels, lights and bumpers.
     *
     * `height` is the *whole* car - roof included - because that is what the
     * collider is now (see `CAR` in src/config/level.ts). The glasshouse is
     * therefore drawn inside it rather than on top of it: a body that filled the
     * box and then grew a cabin would stand half a metre out of its own collider,
     * which in a lot you thread at 30 km/h is the kind of lie that gets somebody
     * killed on the last badly parked car.
     *
     * A `boot` steps the tail down to `boot.height` over the last `boot.length`
     * of it, and that step is the shortcut: it is the one part of a car a full
     * vauhti meter can clear. The drawn step and the collider's step are the same
     * number, taken from the same spec.
     */
    buildCar(
      material: MaterialKey,
      length: number,
      width: number,
      height: number,
      badge?: CarBadge,
      boot: { length: number; height: number; side: -1 | 1 } | null = null,
    ): THREE.Group {
      const group = new THREE.Group();
      const paint = this.assets.material(material);
      const glass = this.assets.material('carGlass');
      const trim = this.assets.material('metal');

      const wheelRadius = Math.min(0.34, height * 0.24);
      const clearance = wheelRadius * 0.55;
      /** Top of the doors and wings: where the paint stops and the glass starts. */
      const beltline = height * CAR_BELTLINE;

      const slab = (
        mat: THREE.Material,
        long: number,
        tall: number,
        across: number,
        x: number,
        y: number,
        z: number,
      ): void => {
        const mesh = new THREE.Mesh(this.assets.boxGeometry, mat);
        mesh.scale.set(long, tall, across);
        mesh.position.set(x, y, z);
        group.add(mesh);
      };

      // Drawn narrower than the collider so the wheels have somewhere to show: a
      // body the full width of the box hides its own wheels inside its
      // silhouette, which is most of why the first pass read as a slab.
      const bodyWidth = width * 0.88;

      // How much of the length is cabin rather than boot lid, and where it sits.
      const bootLength = boot ? boot.length : 0;
      const cabinLength = length - bootLength;
      const cabinCentre = boot ? (-boot.side * bootLength) / 2 : 0;

      // The body proper, standing on its wheels rather than on the ground.
      slab(paint, cabinLength * 0.99, beltline - clearance, bodyWidth, cabinCentre, (beltline + clearance) / 2, 0);

      if (boot) {
        // The lid, and the lip round the back of it. This is the surface a
        // runner with a full meter lands on, so it is drawn at exactly the height
        // the collider stops at and not a centimetre higher.
        const bootCentre = boot.side * (length / 2 - bootLength / 2);
        slab(paint, bootLength, boot.height - clearance, bodyWidth, bootCentre, (boot.height + clearance) / 2, 0);
        slab(trim, bootLength * 0.96, 0.035, bodyWidth * 0.97, bootCentre, boot.height, 0);
        // And the backlight over the step, so the drop reads as a car and not as
        // a car with a bite out of it. Inset its top and bottom from the paint:
        // a glass top at beltline height z-fights with the cabin's exposed deck.
        const paneInset = 0.02;
        slab(
          glass,
          0.04,
          beltline - boot.height - paneInset * 2,
          bodyWidth * 0.9,
          cabinCentre + boot.side * cabinLength * 0.99 / 2,
          (beltline + boot.height) / 2,
          0,
        );
      }

      // Bumpers, proud of the paint at each end.
      for (const end of [-1, 1] as const) {
        const top = boot && end === boot.side ? boot.height : beltline;
        slab(trim, 0.12, top * 0.28, bodyWidth * 0.99, end * (length / 2 - 0.05), top * 0.38, 0);
      }
      // Arches over each wheel, so the gap between body and tyre reads as a car
      // and not as a box on castors.
      for (const end of [-1, 1] as const) {
        slab(paint, wheelRadius * 2.3, clearance * 1.2, width * 0.99, end * length * 0.31, beltline * 0.5, 0);
      }

      // The glasshouse, inside the collider and set back the way a bonnet makes
      // it look. On a car with a boot it sits over the cabin only.
      const cabinHeight = height - beltline;
      const glassCentre = cabinCentre - (boot ? -boot.side : 1) * cabinLength * 0.06;
      slab(glass, cabinLength * 0.6, cabinHeight * 0.84, bodyWidth * 0.96, glassCentre, beltline + cabinHeight * 0.42, 0);
      slab(paint, cabinLength * 0.58, cabinHeight * 0.2, bodyWidth * 0.94, glassCentre, beltline + cabinHeight * 0.92, 0);
      // A- and C-pillars, so the glass reads as windows and not as a blue block.
      for (const end of [-1, 1] as const) {
        for (const side of [-1, 1] as const) {
          slab(
            paint,
            0.07,
            cabinHeight * 0.84,
            bodyWidth * 0.11,
            glassCentre + end * cabinLength * 0.28,
            beltline + cabinHeight * 0.42,
            side * bodyWidth * 0.44,
          );
        }
      }

      // Wheels.
      const tyre = new THREE.CylinderGeometry(wheelRadius, wheelRadius, 0.2, 14);
      const hub = new THREE.CylinderGeometry(wheelRadius * 0.52, wheelRadius * 0.52, 0.22, 12);
      for (const end of [-1, 1]) {
        for (const side of [-1, 1]) {
          const x = end * length * 0.31;
          const z = side * (width / 2 - 0.1);
          for (const [geometry, mat] of [
            [tyre, this.assets.material('tyre')],
            [hub, trim],
          ] as [THREE.BufferGeometry, THREE.Material][]) {
            const wheel = new THREE.Mesh(geometry, mat);
            wheel.rotation.x = Math.PI / 2;
            wheel.position.set(x, wheelRadius, z);
            group.add(wheel);
          }
        }
      }

      // Lights: white at the front, red at the back, each on whatever face it
      // actually has to sit on.
      for (const side of [-1, 1]) {
        const rearTop = boot && boot.side < 0 ? boot.height : beltline;
        const frontTop = boot && boot.side > 0 ? boot.height : beltline;
        slab(this.assets.material('porcelain'), 0.06, frontTop * 0.15, bodyWidth * 0.26, length / 2 - 0.02, frontTop * 0.7, side * bodyWidth * 0.3);
        slab(this.assets.material('lamp'), 0.06, rearTop * 0.15, bodyWidth * 0.28, -length / 2 + 0.02, rearTop * 0.7, side * bodyWidth * 0.29);
      }

      if (badge === 'bmw') this.badgeAsBemari(group, length, width, beltline);
      if (badge === 'audi') this.badgeAsAudi(group, length, width, beltline);
      return group;
    }

    /**
     * Front and rear registration plates, on the bumper under the badge. `y` is
     * where they sit up the vehicle's face - see `CAR_PLATE_Y`.
     *
     * Both ends, because a car with a plate at one end only reads as wrong from
     * whichever side you happen to be on - and the ansalanka is passed from
     * behind and left behind in front. The texture is cached per registration,
     * which is the whole of what a plate costs: the plate is the one thing about
     * a car that is not shared, and it is also the only thing about it that is
     * two triangles and a canvas.
     */
    mountPlates(group: THREE.Group, length: number, y: number, text: string): void {
      let material = this.plates.get(text);
      if (!material) {
        material = new THREE.MeshBasicMaterial({ map: buildPlate(text) });
        this.plates.set(text, material);
      }

      for (const end of [-1, 1]) {
        const mesh = new THREE.Mesh(this.assets.plateGeometry, material);
        // Two centimetres proud of the bumper, the same as the roundel: flush
        // with the paint and the two would z-fight along the whole plate.
        mesh.position.set(end * (length / 2 + 0.02), y, 0);
        mesh.rotation.y = (end * Math.PI) / 2;
        group.add(mesh);
      }
    }

    /**
     * The car on the left of the gap: kidney grille, and the roundel on the nose
     * and the tail.
     *
     * It is the car on charge, so it wants to be identifiable at a glance from
     * across the yard - the whole ansalanka set-piece is built on the player
     * knowing which vehicle the cable belongs to before they are close enough to
     * read the charge flap.
     */
    badgeAsBemari(group: THREE.Group, length: number, width: number, height: number): void {
      if (!this.roundelMaterial) {
        this.roundelMaterial = new THREE.MeshBasicMaterial({ map: buildRoundel() });
      }

      // Kidneys. Two of them, close together, which is the whole of the joke.
      for (const side of [-1, 1]) {
        const kidney = new THREE.Mesh(this.assets.boxGeometry, this.assets.material('tyre'));
        kidney.scale.set(0.05, height * 0.42, width * 0.2);
        kidney.position.set(length / 2 - 0.01, height * 0.52, side * width * 0.13);
        group.add(kidney);
      }

      const radius = Math.min(width, height) * 0.17;
      const badge = new THREE.CircleGeometry(radius, 20);
      for (const end of [-1, 1]) {
        const roundel = new THREE.Mesh(badge, this.roundelMaterial);
        roundel.position.set(end * (length / 2 + 0.02), height * (end > 0 ? 0.82 : 0.74), 0);
        roundel.rotation.y = (end * Math.PI) / 2;
        group.add(roundel);
      }
    }

    /**
     * The car on the right of the gap: one big grille, and four rings over it.
     *
     * The other half of the same job the bemari's roundel does. The gap is
     * `ANSALANKA.slotWidth` of grass between two cars and you take it at a run,
     * so the two cars that frame it have to be told apart at fifty metres and
     * from behind. A grille the full depth of the nose against a pair of kidneys
     * is a silhouette difference, which survives the distance in a way that paint
     * does not.
     */
    badgeAsAudi(group: THREE.Group, length: number, width: number, height: number): void {
      // The singleframe, drawn as a dark panel over a chrome surround: the frame
      // is a slightly larger slab a centimetre further back, which costs one mesh
      // and reads as a border from every angle a runner ever sees it from.
      const frame = new THREE.Mesh(this.assets.boxGeometry, this.assets.material('metal'));
      frame.scale.set(0.04, height * 0.66, width * 0.56);
      frame.position.set(length / 2 - 0.02, height * 0.48, 0);
      group.add(frame);

      const grille = new THREE.Mesh(this.assets.boxGeometry, this.assets.material('tyre'));
      grille.scale.set(0.05, height * 0.58, width * 0.48);
      grille.position.set(length / 2 - 0.01, height * 0.48, 0);
      group.add(grille);

      this.mountRings(group, length, width, height);
    }

    /**
     * Four rings on the nose and the tail, the way the roundel goes on both ends
     * of the bemari and for the same reason: the row is passed from behind and
     * left behind in front.
     *
     * Unlike the roundel this is a plane with alpha in it rather than a disc that
     * fills its own texture, so it is the one badge in the yard that has to be
     * transparent. It does not write depth: a badge two centimetres proud of the
     * paint that punches a hole in the depth buffer would cut the car it is
     * bolted to out of anything drawn after it.
     */
    mountRings(group: THREE.Group, length: number, width: number, height: number): void {
      if (!this.ringsMaterial) {
        this.ringsMaterial = new THREE.MeshBasicMaterial({
          map: buildRings(),
          transparent: true,
          depthWrite: false,
        });
      }

      const badgeWidth = width * 0.42;
      const rings = new THREE.PlaneGeometry(badgeWidth, badgeWidth / RINGS_ASPECT);
      for (const end of [-1, 1]) {
        const mesh = new THREE.Mesh(rings, this.ringsMaterial);
        // Over the grille at the front, on the boot lid at the back - which is
        // where they are on the car, and which keeps them clear of the panel the
        // grille already occupies.
        mesh.position.set(end * (length / 2 + 0.02), height * (end > 0 ? 0.86 : 0.74), 0);
        mesh.rotation.y = (end * Math.PI) / 2;
        group.add(mesh);
      }
    }

    /** A motorhome: one tall box, a luton over the cab, windows and wheels. */
    buildCamper(
      material: MaterialKey,
      length: number,
      width: number,
      height: number,
    ): THREE.Group {
      const group = new THREE.Group();
      const shell = this.assets.material('carEv');
      const paint = this.assets.material(material);
      const wheelRadius = 0.34;
      const wheelWidth = 0.24;
      /** The panes: a windscreen across the cab, and a band down each flank. */
      const windscreen = 0.06;
      const flankPane = 0.05;
      /**
       * How far the habitation box overhangs its own wheels, and how far a pane
       * stands out of the panel it is set into.
       *
       * Neither may be zero, and that is the whole of why they are named. A
       * camper is a stack of slabs that share their edges, and two faces of it
       * landing on one plane is a pixel settled by rounding - which is a wheel
       * that flickers under a body that is not moving, and glass that flickers
       * against the nose behind it. `src/render/coplanar.ts` has what that is and
       * why it cannot be left to the depth buffer. It settles the level's own
       * boxes and it never sees a vehicle, because a vehicle is assembled here
       * out of parts the level does not have; so these say it in metres instead.
       *
       * The sizes are not arbitrary either. Five centimetres of tuck is what a
       * motorhome looks like - the box is the widest thing on it and the wheels
       * stand in its shadow - and glass has to stand *out* of its panel rather
       * than into it, because a pane sunk into a solid slab is a pane nobody
       * sees. A centimetre and a half is a rubber seal's worth.
       */
      const wheelInset = 0.05;
      const paneProud = 0.015;

      const slab = (
        mat: THREE.Material,
        long: number,
        tall: number,
        across: number,
        x: number,
        y: number,
        z: number,
      ): void => {
        const mesh = new THREE.Mesh(this.assets.boxGeometry, mat);
        mesh.scale.set(long, tall, across);
        mesh.position.set(x, y, z);
        group.add(mesh);
      };

      // The habitation box is white; only the cab is painted.
      slab(shell, length * 0.72, height - wheelRadius * 0.6, width, -length * 0.14, (height + wheelRadius * 0.6) / 2, 0);
      slab(paint, length * 0.3, height * 0.55, width * 0.98, length * 0.35, height * 0.36, 0);
      // The luton bed, hanging over the cab. It is what makes one of these read
      // as a motorhome rather than as a van.
      slab(shell, length * 0.34, height * 0.3, width * 0.98, length * 0.33, height * 0.82, 0);
      slab(
        this.assets.material('carGlass'),
        windscreen,
        height * 0.24,
        width * 0.86,
        length / 2 + paneProud - windscreen / 2,
        height * 0.5,
        0,
      );

      // A window band down each flank.
      for (const side of [-1, 1]) {
        slab(
          this.assets.material('carGlass'),
          length * 0.44,
          height * 0.2,
          flankPane,
          -length * 0.16,
          height * 0.62,
          side * (width / 2 + paneProud - flankPane / 2),
        );
      }

      // Tucked under the body rather than flush with it - see `wheelInset`.
      const tyre = new THREE.CylinderGeometry(wheelRadius, wheelRadius, wheelWidth, 14);
      for (const x of [length * 0.3, -length * 0.22, -length * 0.36]) {
        for (const side of [-1, 1]) {
          const wheel = new THREE.Mesh(tyre, this.assets.material('tyre'));
          wheel.rotation.x = Math.PI / 2;
          wheel.position.set(x, wheelRadius, side * (width / 2 - wheelInset - wheelWidth / 2));
          group.add(wheel);
        }
      }

      return group;
    }

    /**
     * The charging cable, as a cable: a tube that sags across the gap and wanders
     * from side to side the way a dropped lead does.
     *
     * Every point of it stays inside the collider box - see `ANSALANKA
     * .cableHalfWidth`. The two ends are allowed above it, because both of them
     * are buried in something solid (the tolppa's socket and the bemari's flap)
     * that the player cannot be standing in anyway.
     */
    cable(spec: SolidSpec): THREE.Mesh {
      const box = spec.box;
      const x = (box.minX + box.maxX) / 2;
      const carEnd = box.maxZ;
      const postEnd = box.minZ;

      // t, sideways wander, height. Authored rather than generated: this is the
      // shape of one specific dropped cable, not a noise function.
      const profile: [number, number, number][] = [
        [0.0, 0.0, 0.47],
        [0.06, 0.05, 0.4],
        [0.2, 0.11, 0.3],
        [0.34, -0.08, 0.26],
        [0.5, 0.1, 0.28],
        [0.66, -0.11, 0.26],
        [0.8, 0.07, 0.31],
        [0.92, -0.03, 0.42],
        [1.0, 0.0, 0.92],
      ];
      const curve = new THREE.CatmullRomCurve3(
        profile.map(([t, offset, y]) => new THREE.Vector3(x + offset, y, carEnd + (postEnd - carEnd) * t)),
      );

      const mesh = new THREE.Mesh(
        new THREE.TubeGeometry(curve, 48, 0.03, 6, false),
        this.assets.material(spec.material),
      );
      mesh.name = spec.name;
      return mesh;
    }
}
